import React, { useState, useEffect, useCallback } from 'react';
import { ConstructionKitDevPanel } from './ConstructionKitDevPanel';

interface DebugPanelProps {
  playerId: string | null;
  apiBaseUrl: string;
  isVisible: boolean;
  onClose: () => void;
  socket?: any; // Socket.IO client instance for sanctum dev commands
}

interface ShipDebugInfo {
  shipId: string;
  ownerId: string;
  ownerName: string;
  ownerType: string;
  position: { x: number; y: number };
  tile: { tileX: number; tileY: number } | null;
  combatState: string;
  currentTarget: string | null;
  hp: number;
  maxHp: number;
  berries: number;
  movement: {
    currentTile: { tileX: number; tileY: number };
    targetTile: { tileX: number; tileY: number } | null;
    pathLength: number;
    path: Array<{ tileX: number; tileY: number }>;
    direction: string;
    movingProgress: number;
    speed: number;
  } | null;
  isBlocked: boolean;
  blockReason: string | null;
}

interface TickStats {
  shipManager: {
    totalShips: number;
    playerShips: number;
    movingShips: number;
    idleShips: number;
    combatShips: number;
    shipTileCacheSize: number;
  };

  islandTileCacheSize: number;
  connectedSockets: number;

  connection?: {
    rtt: number;
    avgRtt: number;
    jitter: number;
    quality: string;
    missedPongs: number;
    syncMultiplier: number;
  } | null;
}

interface TileDebugInfo {
  tile: { tileX: number; tileY: number };
  walkable: boolean;
  blockReason: string | null;
  isIsland: boolean;
}

interface WhirlpoolSummary {
  id: string;
  class: string;
  status: string;
  tileX: number;
  tileY: number;
  x: number;
  y: number;
  expiresAt: number;
  massUsed: number;
  massLimit: number;
}

interface WhirlpoolDebugResponse {
  status: string;
  total: number;
  whirlpools: WhirlpoolSummary[];
}

interface ResourceNodeCountsResponse {
  status: string;
  totalActive: number;
  countsByType: Record<string, number>;
}

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsed: number;
  memoryLimit: number;
  memoryPercent: number;
}

type DebugTab = 'performance' | 'server' | 'ship' | 'world' | 'tools' | 'dev';

export const DebugPanel: React.FC<DebugPanelProps> = ({ playerId, apiBaseUrl, isVisible, onClose, socket }) => {
  const [activeTab, setActiveTab] = useState<DebugTab>('performance');
  const [shipInfo, setShipInfo] = useState<ShipDebugInfo | null>(null);
  const [tickStats, setTickStats] = useState<TickStats | null>(null);
  const [tileCheck, setTileCheck] = useState<TileDebugInfo | null>(null);
  const [whirlpools, setWhirlpools] = useState<WhirlpoolDebugResponse | null>(null);
  const [resourceNodes, setResourceNodes] = useState<ResourceNodeCountsResponse | null>(null);
  const [tileInput, setTileInput] = useState<string>('0,0');
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [adminKey, setAdminKey] = useState<string>('');
  const [isAdminVerified, setIsAdminVerified] = useState<boolean>(false);
  const [isVerifyingAdmin, setIsVerifyingAdmin] = useState<boolean>(false);
  const [perfMetrics, setPerfMetrics] = useState<PerformanceMetrics>({ fps: 0, frameTime: 0, memoryUsed: 0, memoryLimit: 0, memoryPercent: 0 });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sanctumGrade] = useState<'A' | 'B' | 'C' | 'D'>('A');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sanctumRoomCount] = useState<number>(10);
  const [sanctumNumber, setSanctumNumber] = useState<string>('1');
  const [sanctumType, setSanctumType] = useState<'survival' | 'experiment' | 'preservation'>('survival');

  const apiUrl: string = apiBaseUrl;
  const isProdAdminMode: boolean = process.env.NODE_ENV === 'production';
  const isDebugApiEnabled: boolean =
    !isProdAdminMode || process.env.REACT_APP_ENABLE_DEBUG_PANEL === 'true';
  const canUseDebugEndpoints: boolean = isProdAdminMode ? isAdminVerified : isDebugApiEnabled;
  const devBasePath: string = '/api/admin/dev';
  const prodBasePath: string = '/api/admin/prod';
  const effectiveBasePath: string = isProdAdminMode ? prodBasePath : devBasePath;

  const getAdminHeaders = useCallback((): HeadersInit => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (isProdAdminMode && adminKey.trim()) {
      headers['x-admin-key'] = adminKey.trim();
    }
    return headers;
  }, [adminKey, isProdAdminMode]);

  const safeReadErrorMessage = useCallback(async (res: Response): Promise<string> => {
    try {
      const text = await res.text();
      try {
        const parsed = JSON.parse(text) as { error?: string; message?: string };
        return parsed.error || parsed.message || `Request failed (${res.status})`;
      } catch {
        return `Request failed (${res.status})`;
      }
    } catch {
      return `Request failed (${res.status})`;
    }
  }, []);

  const verifyAdminKey = useCallback(async (): Promise<void> => {
    if (!adminKey.trim()) {
      setError('Enter ADMIN_KEY');
      return;
    }

    setIsVerifyingAdmin(true);
    try {
      const res = await fetch(`${apiUrl}${prodBasePath}/verify`, {
        method: 'POST',
        headers: getAdminHeaders(),
      });

      if (res.ok) {
        setIsAdminVerified(true);
        setError(null);
      } else {
        const message = await safeReadErrorMessage(res);
        setIsAdminVerified(false);
        setError(message);
      }
    } catch (err) {
      setIsAdminVerified(false);
      setError('Network error verifying admin key');
    } finally {
      setIsVerifyingAdmin(false);
    }
  }, [adminKey, apiUrl, prodBasePath, safeReadErrorMessage, getAdminHeaders]);

  const fetchShipInfo = useCallback(async (): Promise<void> => {
    if (!canUseDebugEndpoints) return;
    if (!playerId) return;
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-ship/${playerId}`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data: ShipDebugInfo = await res.json();
        setShipInfo(data);
        setError(null);
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      setError('Network error fetching ship info');
    }
  }, [canUseDebugEndpoints, playerId, apiUrl, effectiveBasePath, safeReadErrorMessage, getAdminHeaders]);

  const fetchTickStats = useCallback(async (): Promise<void> => {
    if (!canUseDebugEndpoints) return;
    try {
      const playerQuery = playerId ? `?playerId=${encodeURIComponent(playerId)}` : '';
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-tick-stats${playerQuery}`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data: TickStats = await res.json();
        setTickStats(data);
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      console.error('Failed to fetch tick stats:', err);
    }
  }, [canUseDebugEndpoints, playerId, apiUrl, effectiveBasePath, safeReadErrorMessage, getAdminHeaders]);

  const fetchWhirlpools = useCallback(async (): Promise<void> => {
    if (!canUseDebugEndpoints) return;
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-whirlpools`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data: WhirlpoolDebugResponse = await res.json();
        setWhirlpools(data);
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      console.error('Failed to fetch whirlpools:', err);
    }
  }, [canUseDebugEndpoints, apiUrl, effectiveBasePath, safeReadErrorMessage, getAdminHeaders]);

  const fetchResourceNodes = useCallback(async (): Promise<void> => {
    if (!canUseDebugEndpoints) return;
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-resource-nodes`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data: ResourceNodeCountsResponse = await res.json();
        setResourceNodes(data);
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      console.error('Failed to fetch resource node counts:', err);
    }
  }, [canUseDebugEndpoints, apiUrl, effectiveBasePath, safeReadErrorMessage, getAdminHeaders]);


  const teleportToNearestWhirlpool = async (): Promise<void> => {
    if (!canUseDebugEndpoints) {
      setError(isProdAdminMode ? 'Verify ADMIN_KEY to use debug tools in production' : 'Debug API is disabled');
      return;
    }
    if (!playerId) return;
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/teleport-to-nearest-whirlpool`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({ playerId }),
      });
      if (res.ok) {
        setError(null);
        await fetchShipInfo();
        await fetchWhirlpools();
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      setError('Network error teleporting to whirlpool');
    }
  };

  const checkTile = async (): Promise<void> => {
    if (!canUseDebugEndpoints) {
      setError(isProdAdminMode ? 'Verify ADMIN_KEY to use debug tools in production' : 'Debug API is disabled');
      return;
    }
    const [tileX, tileY] = tileInput.split(',').map(s => parseInt(s.trim()));
    if (isNaN(tileX) || isNaN(tileY)) {
      setError('Invalid tile format. Use: x,y');
      return;
    }
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-tile/${tileX}/${tileY}`, {
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data: TileDebugInfo = await res.json();
        setTileCheck(data);
        setError(null);
      } else {
        const message = await safeReadErrorMessage(res);
        setError(message);
      }
    } catch (err) {
      setError('Failed to check tile');
    }
  };

  const forceMove = async (): Promise<void> => {
    if (!canUseDebugEndpoints) {
      setError(isProdAdminMode ? 'Verify ADMIN_KEY to use debug tools in production' : 'Debug API is disabled');
      return;
    }
    if (!playerId) return;
    try {
      const res = await fetch(`${apiUrl}${effectiveBasePath}/debug-force-move`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({ playerId }),
      });
      const text = await res.text();
      const data = (() => {
        try {
          return JSON.parse(text) as { error?: string };
        } catch {
          return {} as { error?: string };
        }
      })();
      if (res.ok) {
        setError(null);
        await fetchShipInfo();
      } else {
        setError(data.error || 'Failed to force move');
      }
    } catch (err) {
      setError('Network error during force move');
    }
  };

  // Track FPS and performance metrics
  useEffect(() => {
    if (!isVisible) return;

    let frameCount = 0;
    let lastTime = window.performance.now();
    let animationFrameId: number;

    const trackPerformance = (): void => {
      frameCount++;
      const currentTime = window.performance.now();
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / deltaTime);
        const frameTime = deltaTime / frameCount;

        // Get memory usage if available
        let memoryUsed = 0;
        let memoryLimit = 0;
        let memoryPercent = 0;

        if ((window.performance as any).memory) {
          memoryUsed = Math.round((window.performance as any).memory.usedJSHeapSize / 1048576);
          memoryLimit = Math.round((window.performance as any).memory.jsHeapSizeLimit / 1048576);
          memoryPercent = Math.round(((window.performance as any).memory.usedJSHeapSize / (window.performance as any).memory.jsHeapSizeLimit) * 100);
        }

        setPerfMetrics({
          fps,
          frameTime: Math.round(frameTime * 100) / 100,
          memoryUsed,
          memoryLimit,
          memoryPercent,
        });

        frameCount = 0;
        lastTime = currentTime;
      }

      animationFrameId = requestAnimationFrame(trackPerformance);
    };

    animationFrameId = requestAnimationFrame(trackPerformance);

    return () => cancelAnimationFrame(animationFrameId);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !autoRefresh || !canUseDebugEndpoints) return;
    
    fetchShipInfo();
    fetchTickStats();
    
    const interval = setInterval(() => {
      fetchShipInfo();
      fetchTickStats();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isVisible, autoRefresh, canUseDebugEndpoints, fetchShipInfo, fetchTickStats]);

  useEffect(() => {
    if (!isVisible || !autoRefresh || !canUseDebugEndpoints) return;

    fetchWhirlpools();
    fetchResourceNodes();

    const interval = setInterval(() => {
      fetchWhirlpools();
      fetchResourceNodes();
    }, 5000);

    return () => clearInterval(interval);
  }, [isVisible, autoRefresh, canUseDebugEndpoints, fetchWhirlpools, fetchResourceNodes]);

  if (!isVisible) return null;

  const tabStyle = (isActive: boolean) => ({
    padding: '6px 12px',
    backgroundColor: isActive ? '#3a3a5a' : '#2a2a4e',
    border: isActive ? '1px solid #87ceeb' : '1px solid #4a4a6a',
    color: isActive ? '#87ceeb' : '#b0b0b0',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: isActive ? 'bold' : 'normal',
    borderBottom: isActive ? 'none' : '1px solid #4a4a6a',
  });

  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      left: '10px',
      width: '420px',
      maxHeight: '90vh',
      backgroundColor: '#1a1a2e',
      border: '2px solid #4a4a6a',
      color: '#e0e0e0',
      fontFamily: '"Sometype Mono", monospace',
      fontSize: '11px',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px',
        backgroundColor: '#2a2a4e',
        borderBottom: '1px solid #4a4a6a',
      }}>
        <span style={{ fontWeight: 'bold', color: '#ffd700' }}>DEBUG PANEL</span>
        <div>
          <label style={{ marginRight: '10px', fontSize: '10px' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto
          </label>
          <button
            onClick={onClose}
            style={{
              background: '#e74c3c',
              border: 'none',
              color: 'white',
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: '10px',
            }}
          >
            X
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '2px',
        backgroundColor: '#1a1a2e',
        borderBottom: '1px solid #4a4a6a',
        padding: '4px',
        overflowX: 'auto',
      }}>
        <button onClick={() => setActiveTab('performance')} style={tabStyle(activeTab === 'performance')}>
          📊 Perf
        </button>
        <button onClick={() => setActiveTab('server')} style={tabStyle(activeTab === 'server')}>
          🖥️ Server
        </button>
        <button onClick={() => setActiveTab('ship')} style={tabStyle(activeTab === 'ship')}>
          🚢 Ship
        </button>
        <button onClick={() => setActiveTab('world')} style={tabStyle(activeTab === 'world')}>
          🌍 World
        </button>
        <button onClick={() => setActiveTab('tools')} style={tabStyle(activeTab === 'tools')}>
          🔧 Tools
        </button>
        <button onClick={() => setActiveTab('dev')} style={tabStyle(activeTab === 'dev')}>
          🔨 Dev
        </button>
      </div>

      {/* Content Area - Scrollable */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '8px',
      }}>
        {error && (
          <div style={{ padding: '6px', backgroundColor: '#4a1a1a', color: '#ff6b6b', borderRadius: '4px', marginBottom: '8px', fontSize: '10px' }}>
            {error}
          </div>
        )}

        {/* PERFORMANCE TAB */}
        {activeTab === 'performance' && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>📊 Client Performance</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '10px' }}>
                <span style={{ color: perfMetrics.fps >= 50 ? '#2ecc71' : perfMetrics.fps >= 30 ? '#f39c12' : '#e74c3c' }}>
                  FPS: {perfMetrics.fps}
                </span>
                <span>Frame: {perfMetrics.frameTime}ms</span>
                {perfMetrics.memoryUsed > 0 && (
                  <>
                    <span style={{ color: perfMetrics.memoryPercent > 80 ? '#e74c3c' : perfMetrics.memoryPercent > 60 ? '#f39c12' : '#2ecc71' }}>
                      Memory: {perfMetrics.memoryUsed}MB
                    </span>
                    <span style={{ color: perfMetrics.memoryPercent > 80 ? '#e74c3c' : perfMetrics.memoryPercent > 60 ? '#f39c12' : '#2ecc71' }}>
                      Usage: {perfMetrics.memoryPercent}%
                    </span>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* SERVER TAB */}
        {activeTab === 'server' && (
          <>
            {isProdAdminMode && (
              <div style={{ marginBottom: '12px', padding: '6px', backgroundColor: '#2a2a4e', borderRadius: '4px' }}>
                <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px', fontSize: '10px' }}>🔐 Admin Key</div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input
                    type="password"
                    value={adminKey}
                    onChange={(e) => {
                      setAdminKey(e.target.value);
                      setIsAdminVerified(false);
                    }}
                    placeholder="ADMIN_KEY"
                    style={{
                      flex: 1,
                      padding: '4px',
                      backgroundColor: '#1a1a3e',
                      border: '1px solid #4a4a6a',
                      color: '#e0e0e0',
                      fontSize: '9px',
                    }}
                  />
                  <button
                    onClick={verifyAdminKey}
                    disabled={isVerifyingAdmin || adminKey.trim().length === 0}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: isAdminVerified ? '#2ecc71' : '#3498db',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '9px',
                      opacity: (isVerifyingAdmin || adminKey.trim().length === 0) ? 0.6 : 1,
                    }}
                  >
                    {isVerifyingAdmin ? '...' : (isAdminVerified ? '✓' : 'Verify')}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>🖥️ Server Stats</div>
              {tickStats ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '10px' }}>
                  <span>Ships: {tickStats.shipManager.totalShips}</span>
                  <span>Players: {tickStats.shipManager.playerShips}</span>
                  <span>Moving: {tickStats.shipManager.movingShips}</span>
                  <span>Idle: {tickStats.shipManager.idleShips}</span>
                  <span>Combat: {tickStats.shipManager.combatShips}</span>
                  <span>Island Tiles: {tickStats.islandTileCacheSize}</span>
                  <span>Ship Tiles: {tickStats.shipManager.shipTileCacheSize}</span>
                  <span>Sockets: {tickStats.connectedSockets}</span>
                  <span>Conn: {tickStats.connection ? `${tickStats.connection.avgRtt}ms` : 'N/A'}</span>
                  <span>Jitter: {tickStats.connection ? `${tickStats.connection.jitter}ms` : 'N/A'}</span>
                  <span>Quality: {tickStats.connection?.quality || 'N/A'}</span>
                </div>
              ) : (
                <span style={{ fontSize: '10px' }}>Loading...</span>
              )}
            </div>
          </>
        )}

        {/* SHIP TAB */}
        {activeTab === 'ship' && (
          <>
            <div style={{ fontSize: '9px', color: '#718096', marginBottom: '8px' }}>
              ID: {playerId ? playerId.substring(0, 12) + '...' : 'NOT CONNECTED'}
            </div>
            {!playerId ? (
              <div style={{ color: '#e74c3c', fontSize: '10px' }}>
                Socket not connected
              </div>
            ) : shipInfo ? (
              <div style={{ fontSize: '10px' }}>
                <div>Ship: {shipInfo.shipId.substring(0, 16)}...</div>
                <div>Pos: ({Math.round(shipInfo.position.x)}, {Math.round(shipInfo.position.y)})</div>
                <div>Tile: {shipInfo.tile ? `(${shipInfo.tile.tileX}, ${shipInfo.tile.tileY})` : 'N/A'}</div>
                <div>Combat: <span style={{ color: shipInfo.combatState === 'idle' ? '#2ecc71' : '#e74c3c' }}>{shipInfo.combatState}</span></div>
                <div>HP: {shipInfo.hp}/{shipInfo.maxHp}</div>
                <div>Pieces: {shipInfo.berries}</div>
                
                {shipInfo.movement && (
                  <>
                    <div style={{ marginTop: '6px', fontWeight: 'bold', color: '#f39c12' }}>Movement</div>
                    <div>Path: {shipInfo.movement.pathLength}</div>
                    <div>Progress: {(shipInfo.movement.movingProgress * 100).toFixed(0)}%</div>
                    <div>Speed: {shipInfo.movement.speed} t/s</div>
                    <div>Blocked: {shipInfo.isBlocked ? `YES (${shipInfo.blockReason})` : 'NO'}</div>
                  </>
                )}
                
                <button
                  onClick={forceMove}
                  style={{
                    marginTop: '8px',
                    padding: '4px 8px',
                    backgroundColor: '#3498db',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    fontSize: '10px',
                  }}
                  disabled={!shipInfo.movement || shipInfo.movement.path.length === 0}
                >
                  Force Move
                </button>

                <button
                  onClick={teleportToNearestWhirlpool}
                  style={{
                    marginTop: '4px',
                    padding: '4px 8px',
                    backgroundColor: '#553c9a',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    fontSize: '10px',
                  }}
                  disabled={!playerId}
                >
                  Nearest Whirlpool
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '10px' }}>Loading...</div>
            )}
          </>
        )}

        {/* WORLD TAB */}
        {activeTab === 'world' && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>🌀 Whirlpools</div>
              {whirlpools ? (
                <div style={{ fontSize: '10px' }}>
                  <div>Total: {whirlpools.total}</div>
                  {whirlpools.whirlpools.length > 0 && (
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '4px' }}>
                      {whirlpools.whirlpools.slice(0, 3).map((w, i) => (
                        <div key={i}>{w.class} ({w.x},{w.y})</div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ fontSize: '10px' }}>Loading...</span>
              )}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>⛏️ Resources</div>
              {resourceNodes ? (
                <div style={{ fontSize: '10px' }}>
                  <div>Total: {resourceNodes.totalActive}</div>
                  <div style={{ fontSize: '9px', color: '#888', marginTop: '4px' }}>
                    {Object.entries(resourceNodes.countsByType)
                      .filter(([, v]) => v > 0)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 4)
                      .map(([k, v]) => `${k}:${v}`)
                      .join('  ')}
                  </div>
                </div>
              ) : (
                <span style={{ fontSize: '10px' }}>Loading...</span>
              )}
            </div>
          </>
        )}

        {/* TOOLS TAB */}
        {activeTab === 'tools' && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>🏛️ Sanctum</div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                <input
                  type="number"
                  value={sanctumNumber}
                  onChange={(e) => setSanctumNumber(e.target.value)}
                  placeholder="#"
                  style={{
                    flex: 1,
                    padding: '4px',
                    backgroundColor: '#2a2a4e',
                    border: '1px solid #4a4a6a',
                    color: '#e0e0e0',
                    fontSize: '10px',
                  }}
                  min="1"
                />
                <select
                  value={sanctumType}
                  onChange={(e) => setSanctumType(e.target.value as 'survival' | 'experiment' | 'preservation')}
                  style={{
                    padding: '4px',
                    backgroundColor: '#2a2a4e',
                    border: '1px solid #4a4a6a',
                    color: '#e0e0e0',
                    fontSize: '10px',
                  }}
                >
                  <option value="survival">Survival</option>
                  <option value="experiment">Experiment</option>
                  <option value="preservation">Preservation</option>
                </select>
              </div>
              <div style={{ fontSize: '9px', color: '#888' }}>
                Sanctum-{sanctumNumber} ({sanctumType})
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#87ceeb', marginBottom: '4px' }}>🔲 Tile Checker</div>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                <input
                  type="text"
                  value={tileInput}
                  onChange={(e) => setTileInput(e.target.value)}
                  placeholder="x,y"
                  style={{
                    flex: 1,
                    padding: '4px',
                    backgroundColor: '#2a2a4e',
                    border: '1px solid #4a4a6a',
                    color: '#e0e0e0',
                    fontSize: '10px',
                  }}
                />
                <button
                  onClick={checkTile}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#9b59b6',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '10px',
                  }}
                >
                  Check
                </button>
              </div>
              {tileCheck && (
                <div style={{ fontSize: '10px' }}>
                  <div>Tile: ({tileCheck.tile.tileX}, {tileCheck.tile.tileY})</div>
                  <div>Walkable: <span style={{ color: tileCheck.walkable ? '#2ecc71' : '#e74c3c' }}>
                    {tileCheck.walkable ? 'YES' : 'NO'}
                  </span></div>
                  <div>Island: {tileCheck.isIsland ? 'YES' : 'NO'}</div>
                  {tileCheck.blockReason && <div>Reason: {tileCheck.blockReason}</div>}
                </div>
              )}
            </div>
          </>
        )}

        {/* DEV TAB */}
        {activeTab === 'dev' && (
          <ConstructionKitDevPanel
            socket={socket}
            playerId={playerId || undefined}
            apiBaseUrl={apiBaseUrl}
            isAdminVerified={isAdminVerified}
            isProdMode={isProdAdminMode}
          />
        )}
      </div>
    </div>
  );
};
